'use client';

import { useState } from 'react';
import { useAuthContext } from '@/contexts/AuthContext';
import {
  createUserDocument,
  signIn as firebaseSignIn,
  signInWithGoogle as firebaseSignInWithGoogle,
  signOut as firebaseSignOut,
  signUp as firebaseSignUp,
} from '@/lib/firebase/auth';

export function useAuth() {
  const { user, userProfile, couple, coupleId, loading, error: authError, loadState, refreshToken: contextRefreshToken } = useAuthContext();
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const signUp = async (email: string, password: string, displayName: string) => {
    try {
      setIsLoading(true);
      setError(null);
      const result = await firebaseSignUp(email, password);
      await createUserDocument(result.user, { displayName });
      return result;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to sign up';
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      setIsLoading(true);
      setError(null);
      const result = await firebaseSignIn(email, password);
      return result;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to sign in';
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const signInWithGoogle = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const result = await firebaseSignInWithGoogle();
      await createUserDocument(result.user);
      return result;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to sign in with Google';
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const signOut = async () => {
    try {
      setIsLoading(true);
      setError(null);
      await firebaseSignOut();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to sign out';
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const refreshToken = async () => {
    try {
      await contextRefreshToken();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to refresh token';
      setError(message);
      throw err;
    }
  };

  return {
    user,
    userProfile,
    couple,
    coupleId,
    loading: loading || isLoading,
    error: error || authError,
    signUp,
    signIn,
    signInWithGoogle,
    signOut,
    refreshToken,
    loadState
  };
}
